/**
 * Cronometro autoritativo por TIMESTAMP (spec 4.6).
 *
 * A regra que define este arquivo: o tempo restante e sempre DERIVADO de
 * `started_at`, nunca acumulado. O `setInterval` daqui existe apenas para
 * forcar um novo render a cada segundo — ele nao soma nada, e portanto nao
 * pode ficar para tras.
 *
 * E isso que faz o timer sobreviver a lock de tela, troca de aba e recarga da
 * pagina: navegadores estrangulam timers em segundo plano (um `setInterval` de
 * 1 s vira 1/min ou para de vez), mas `Date.now() - started_at` continua
 * verdadeiro o tempo todo. Ao voltar, o valor certo aparece no primeiro frame.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  POMODORO_DEFAULTS,
  uuidv7,
  type PomodoroMode,
  type PomodoroSession,
} from '@cleartask/shared';
import { api } from '../../lib/api.js';
import { useCurrentUser } from '../../session.js';

export const pomodoroKeys = {
  all: ['pomodoro'] as const,
  active: ['pomodoro', 'active'] as const,
  stats: (range: 'week' | 'month') => ['pomodoro', 'stats', range] as const,
};

/**
 * Forca um render por segundo.
 *
 * Deliberadamente NAO mantem contador: se o navegador estrangular o intervalo
 * enquanto a aba esta oculta, nada se perde — na volta, o proximo render
 * recalcula tudo a partir do timestamp.
 */
function useSecondTick(enabled: boolean): void {
  const [, force] = useState(0);

  useEffect(() => {
    if (!enabled) return;

    const id = setInterval(() => force((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [enabled]);
}

/** Duracoes configuradas pelo usuario, com o padrao da spec como piso. */
function useDurations(): Record<PomodoroMode, number> {
  const user = useCurrentUser();
  const config = user.settings?.pomodoro;

  return {
    focus: config?.focusSeconds ?? POMODORO_DEFAULTS.focusSeconds,
    short_break: config?.shortBreakSeconds ?? POMODORO_DEFAULTS.shortBreakSeconds,
    long_break: config?.longBreakSeconds ?? POMODORO_DEFAULTS.longBreakSeconds,
    // Cronometro conta para cima: nao tem duracao planejada.
    stopwatch: 0,
  };
}

export interface PomodoroController {
  session: PomodoroSession | null;
  /** Segundos decorridos desde `started_at`, corrigidos pelo relogio do servidor. */
  elapsedS: number;
  /** Segundos restantes. Sempre 0 no modo cronometro. */
  remainingS: number;
  /** 0..1 — quanto do ciclo ja passou. */
  progress: number;
  cyclesBeforeLongBreak: number;
  durations: Record<PomodoroMode, number>;
  isPending: boolean;
  start: (mode: PomodoroMode, taskId: string | null) => void;
  /**
   * @param note Anotacao a gravar. Omitida, o controlador le a caixa por
   *   `obterNota` — assim um encerramento por atalho nao descarta o texto.
   */
  stop: (completed: boolean, note?: string | null) => void;
}

/**
 * @param obterNota Le a anotacao em aberto no momento do encerramento. E uma
 *   funcao, e nao um valor, porque o ciclo tambem termina SOZINHO: um valor
 *   capturado na renderizacao estaria congelado no que existia quando o efeito
 *   foi montado, e a nota escrita depois disso se perderia — em silencio, no
 *   caso mais comum, que e o pomodoro chegando ao fim sem ninguem clicar nada.
 */
export function usePomodoro(obterNota?: () => string | null): PomodoroController {
  const qc = useQueryClient();
  const user = useCurrentUser();
  const durations = useDurations();

  const { data, isPending } = useQuery({
    queryKey: pomodoroKeys.active,
    queryFn: () => api.pomodoro.active(),
    // Um cronometro nao pode servir cache: ao voltar para a aba, o estado
    // precisa vir do servidor, que e quem sabe se a sessao ainda existe.
    staleTime: 0,
    refetchOnWindowFocus: true,
  });

  const session = data?.session ?? null;

  /**
   * Diferenca entre o relogio do servidor e o do dispositivo.
   *
   * Sem esta correcao, um aparelho adiantado em 3 minutos mostraria um
   * pomodoro de 22 — o `started_at` autoritativo nao adiantaria de nada se o
   * "agora" usado para subtrair estivesse errado.
   */
  const skew = useRef(0);
  const ultimaResposta = useRef<unknown>(null);

  // Calculado DURANTE o render, e nao num efeito. Num `useEffect` o valor so
  // existiria depois da primeira pintura: o timer apareceria errado por ate um
  // segundo, ate o proximo tick corrigir — bem no momento em que a pessoa
  // volta para a aba e olha para o numero.
  if (data && data !== ultimaResposta.current) {
    ultimaResposta.current = data;
    skew.current = data.now - Date.now();
  }

  useSecondTick(session !== null);

  const agora = Date.now() + skew.current;
  const elapsedS = session ? Math.max(0, Math.floor((agora - session.startedAt) / 1000)) : 0;
  const remainingS =
    session && session.plannedS > 0 ? Math.max(0, session.plannedS - elapsedS) : 0;

  const progress =
    session && session.plannedS > 0 ? Math.min(1, elapsedS / session.plannedS) : 0;

  const invalidate = useCallback(() => {
    void qc.invalidateQueries({ queryKey: pomodoroKeys.all });
  }, [qc]);

  const startMutation = useMutation({
    mutationFn: (input: { mode: PomodoroMode; taskId: string | null; plannedS: number }) =>
      api.pomodoro.start({
        id: uuidv7(),
        mode: input.mode,
        taskId: input.taskId,
        startedAt: Date.now() + skew.current,
        plannedS: input.plannedS,
      }),
    onSuccess: invalidate,
  });

  const endMutation = useMutation({
    mutationFn: (input: {
      id: string;
      durationS: number;
      completed: boolean;
      note: string | null;
    }) =>
      api.pomodoro.end(input.id, {
        endedAt: Date.now() + skew.current,
        durationS: input.durationS,
        completed: input.completed,
        note: input.note,
      }),
    onSuccess: invalidate,
  });

  const start = useCallback(
    (mode: PomodoroMode, taskId: string | null) => {
      startMutation.mutate({ mode, taskId, plannedS: durations[mode] });
    },
    [durations, startMutation],
  );

  const stop = useCallback(
    (completed: boolean, note?: string | null) => {
      if (!session) return;
      // Quem chama sem nota (encerramento por atalho) ainda assim grava o que
      // estiver escrito na caixa.
      note = note ?? obterNota?.() ?? null;
      // Nota em branco vira `null`: "nao escrevi" e "apaguei o que escrevi" nao
      // sao estados diferentes, e uma string vazia obrigaria a UI a distinguir
      // os dois na leitura.
      const limpa = note?.trim();
      endMutation.mutate({
        id: session.id,
        durationS: elapsedS,
        completed,
        note: limpa ? limpa : null,
      });
    },
    [elapsedS, endMutation, obterNota, session],
  );

  /**
   * Fecha a sessao sozinha quando o ciclo chega ao fim.
   *
   * O `ref` guarda o id ja encerrado porque este efeito roda a cada tick: sem
   * ele, o encerramento seria reenviado uma vez por segundo ate a invalidacao
   * chegar.
   *
   * A PAUSA seguinte nao comeca automaticamente. O ciclo termina justamente
   * quando a pessoa pode ter saido da mesa; iniciar um descanso que ela nao
   * pediu registraria tempo inventado no lugar de tempo real.
   */
  const autoEncerrada = useRef<string | null>(null);
  useEffect(() => {
    if (!session || session.plannedS <= 0) return;
    if (remainingS > 0) return;
    if (autoEncerrada.current === session.id) return;

    autoEncerrada.current = session.id;

    const nota = obterNota?.()?.trim();
    endMutation.mutate({
      id: session.id,
      durationS: session.plannedS,
      completed: true,
      note: nota ? nota : null,
    });
  }, [endMutation, obterNota, remainingS, session]);

  return {
    session,
    elapsedS,
    remainingS,
    progress,
    cyclesBeforeLongBreak:
      user.settings?.pomodoro?.cyclesBeforeLongBreak ??
      POMODORO_DEFAULTS.cyclesBeforeLongBreak,
    durations,
    isPending,
    start,
    stop,
  };
}

export function usePomodoroStats(range: 'week' | 'month') {
  return useQuery({
    queryKey: pomodoroKeys.stats(range),
    queryFn: () => api.pomodoro.stats(range),
    staleTime: 60_000,
  });
}

/** Segundos -> 'MM:SS', ou 'H:MM:SS' quando passa de uma hora. */
export function formatClock(totalSeconds: number): string {
  const seguro = Math.max(0, Math.floor(totalSeconds));
  const horas = Math.floor(seguro / 3600);
  const minutos = Math.floor((seguro % 3600) / 60);
  const segundos = seguro % 60;

  const mm = String(minutos).padStart(2, '0');
  const ss = String(segundos).padStart(2, '0');

  return horas > 0 ? `${horas}:${mm}:${ss}` : `${mm}:${ss}`;
}

/** Segundos -> '3h20' / '45min', para os resumos de foco. */
export function formatDuration(totalSeconds: number): string {
  const minutosTotais = Math.floor(Math.max(0, totalSeconds) / 60);
  const horas = Math.floor(minutosTotais / 60);
  const minutos = minutosTotais % 60;

  if (horas === 0) return `${minutos}min`;
  return minutos === 0 ? `${horas}h` : `${horas}h${String(minutos).padStart(2, '0')}`;
}
